import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    CalendarDays,
    Camera as CameraIcon,
    ChevronLeft,
    ChevronRight,
    Eye,
    ImagePlus,
    Plus,
    ShieldCheck,
    UserCheck,
    X,
} from 'lucide-react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { activeChildDetail, parentProfile } from '../../atomstore/store';
import { useRequestProcessor } from '../../requestProcessor';
import { getVisitorPickupCards, saveVisitorPickupCard, activateVisitorPickup, deactivateVisitorPickup } from '../../queryfunctions/queryfunctions';
import { setAlertModal } from '../../atomstore/serviceAtoms';
import SpinnerLoading from '../common/SpinnerLoading';

const EMPTY_FORM = {
    name: '',
    relationship: '',
    photo: '',
};

const FALLBACK_RELATIONSHIP_OPTIONS = [
    'Grandfather',
    'Grandmother',
    'Uncle',
    'Aunt',
    'Family Friend',
    'Other',
];

const todayString = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
};

const isVisitorPickupSuccess = (result) => result?.status === 'ok' || result?.status === 'success';

const getActiveRequests = (card) => (
    Array.isArray(card?.active_requests)
        ? card.active_requests
        : card?.active_request
            ? [card.active_request]
            : []
).filter((item) => item?.request_id || item?.pickup_date);

const showVisitorPickupAlert = (message, type = 'Error') => {
    setAlertModal({
        state: true,
        type,
        isConfirm: false,
        message,
        navigateTo: null,
        onOkPressed: () => { },
        onCancelPressed: () => { },
    });
};

const resizeImage = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const image = new Image();
            image.onerror = reject;
            image.onload = () => {
                const maxSide = 900;
                const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(image.width * scale);
                canvas.height = Math.round(image.height * scale);
                canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.78));
            };
            image.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

const createCroppedPhoto = (source, cropPixels) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onerror = reject;
        image.onload = () => {
            const outputSize = 600;
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = outputSize;
            canvas.height = outputSize;
            context.drawImage(
                image,
                cropPixels.x,
                cropPixels.y,
                cropPixels.width,
                cropPixels.height,
                0,
                0,
                outputSize,
                outputSize
            );
            resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        image.src = source;
    });

const InlinePickupCalendar = ({
    value,
    onChange,
    allowedDates = [],
    unavailableDates = [],
    ownedDates = [],
    lockedDates = [],
}) => {
    const dateItems = (allowedDates || []).map((item) => (
        typeof item === 'string' ? { date: item, selectable: true, reason: null } : item
    )).filter((item) => item?.date);
    const dateLookup = Object.fromEntries(dateItems.map((item) => [item.date, item]));
    const windowDates = dateItems.map((item) => item.date).sort();
    const minimumDate = windowDates[0] || todayString();
    const maximumDate = windowDates[windowDates.length - 1] || minimumDate;
    const selectedDates = Array.isArray(value) ? value : value ? [value] : [];
    const focusDate = selectedDates[0] || minimumDate;
    const [visibleMonth, setVisibleMonth] = useState(() => {
        const date = new Date(`${focusDate}T00:00:00`);
        return new Date(date.getFullYear(), date.getMonth(), 1);
    });

    useEffect(() => {
        const date = new Date(`${focusDate}T00:00:00`);
        setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }, [focusDate]);

    const calendarDays = useMemo(() => {
        const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
        const gridStart = new Date(firstDay);
        gridStart.setDate(firstDay.getDate() - firstDay.getDay());

        return Array.from({ length: 42 }, (_, index) => {
            const date = new Date(gridStart);
            date.setDate(gridStart.getDate() + index);
            const offset = date.getTimezoneOffset();
            const dateString = new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
            return {
                date,
                dateString,
                isCurrentMonth: date.getMonth() === visibleMonth.getMonth(),
            };
        });
    }, [visibleMonth]);

    const monthKey = `${visibleMonth.getFullYear()}-${String(visibleMonth.getMonth() + 1).padStart(2, '0')}`;
    const minimumMonth = minimumDate.slice(0, 7);
    const maximumMonth = maximumDate.slice(0, 7);
    const changeMonth = (amount) => {
        setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => changeMonth(-1)}
                    disabled={monthKey <= minimumMonth}
                    className="rounded-full p-2 text-gray-600 active:bg-gray-100 disabled:text-gray-300"
                    aria-label="Previous month"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <h3 className="text-sm font-bold text-gray-900">
                    {new Intl.DateTimeFormat('en-IN', {
                        month: 'long',
                        year: 'numeric',
                    }).format(visibleMonth)}
                </h3>
                <button
                    type="button"
                    onClick={() => changeMonth(1)}
                    disabled={monthKey >= maximumMonth}
                    className="rounded-full p-2 text-gray-600 active:bg-gray-100 disabled:text-gray-300"
                    aria-label="Next month"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>

            <div className="grid grid-cols-7 text-center">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <span key={day} className="pb-2 text-[10px] font-bold uppercase text-gray-400">
                        {day}
                    </span>
                ))}
                {calendarDays.map(({ date, dateString, isCurrentMonth }) => {
                    const allowedDate = dateLookup[dateString];
                    const isOwned = ownedDates.includes(dateString);
                    const isUnavailable = unavailableDates.includes(dateString);
                    const isLocked = lockedDates.includes(dateString);
                    const isClosed = Boolean(allowedDate) && allowedDate.selectable === false && !isOwned;
                    const isAvailable = (allowedDate?.selectable === true || isOwned) && !isUnavailable;
                    const isSelected = selectedDates.includes(dateString);
                    const isToday = dateString === todayString();
                    const disabledReason = isLocked
                        ? 'Deactivation is no longer available for this pickup'
                        : isUnavailable
                            ? 'Already scheduled for another pickup person'
                            : isOwned
                                ? undefined
                                : allowedDate?.reason || undefined;

                    return (
                        <button
                            type="button"
                            key={dateString}
                            disabled={!isAvailable || (isSelected && isLocked)}
                            onClick={() => {
                                const nextDates = isSelected
                                    ? selectedDates.filter((item) => item !== dateString)
                                    : [...selectedDates, dateString].sort();
                                onChange(nextDates);
                            }}
                            className={`relative mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition md:h-11 md:w-11 ${
                                isSelected && isOwned
                                    ? 'bg-amber-500 text-white shadow-md'
                                    : isSelected
                                        ? 'bg-cyan-500 text-white shadow-md'
                                        : isUnavailable || isClosed
                                            ? 'cursor-not-allowed bg-gray-100 text-gray-400 line-through'
                                            : isAvailable
                                                ? 'text-gray-800 active:bg-cyan-50'
                                                : 'cursor-not-allowed text-gray-300'
                            } ${!isCurrentMonth && !(isSelected && isOwned) && !isSelected ? 'opacity-60' : ''} ${
                                isToday && !isSelected && isAvailable ? 'ring-1 ring-cyan-500' : ''
                            }`}
                            aria-label={new Intl.DateTimeFormat('en-IN', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                            }).format(date)}
                            aria-pressed={isSelected}
                            title={disabledReason}
                        >
                            {date.getDate()}
                            {isToday && !isSelected && (
                                <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-cyan-500" />
                            )}
                        </button>
                    );
                })}
            </div>
            {ownedDates.length > 0 && (
                <div className="mt-3 flex items-center gap-4 px-1 text-[11px] font-medium text-gray-500">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                        Already scheduled
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-cyan-500" />
                        New date
                    </span>
                </div>
            )}
        </div>
    );
};

const PickupAuthorizations = () => {
    const navigate = useNavigate();
    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [parent] = useAtom(parentProfile);
    const parentEmail =
        parent?.parentEmail ||
        (activeChild?.parent === 'Mother' ? activeChild?.mother_email : activeChild?.father_email) ||
        '';
    const cardsQuery = useQueryWrapper(
        ['visitorPickupCards', activeChild?.id, parentEmail],
        () => getVisitorPickupCards(activeChild, parentEmail),
        {
            enabled: !!activeChild?.id && !!parentEmail,
            refetchOnWindowFocus: false,
        }
    );
    const pickupPayload = isVisitorPickupSuccess(cardsQuery.data)
        ? (cardsQuery.data.data || cardsQuery.data.response || null)
        : null;
    const cardsError =
        !activeChild?.id
            ? 'Child id is missing.'
            : !parentEmail
                ? 'Parent email is missing.'
                : cardsQuery.data?.status === 'error'
                    ? cardsQuery.data.error || 'Unable to load visitor pickup cards.'
                    : cardsQuery.error?.message || (cardsQuery.isError ? 'Unable to load visitor pickup cards.' : '');
    const cards = pickupPayload?.cards || [];
    const maxCards = Number(pickupPayload?.max_cards) > 0 ? Number(pickupPayload.max_cards) : 3;
    const relationshipOptions = pickupPayload?.relationship_options?.length
        ? pickupPayload.relationship_options
        : FALLBACK_RELATIONSHIP_OPTIONS;
    const allowedDates = (pickupPayload?.allowed_dates || []).filter((item) => item?.date);
    const selectableDates = allowedDates.filter((item) => item.selectable).map((item) => item.date);
    const childName = pickupPayload?.child?.child_name
        || `${activeChild?.name || ''} ${activeChild?.last_name || ''}`.trim();
    const programLabel = [
        pickupPayload?.child?.program_name || activeChild?.length || 'Program',
        pickupPayload?.child?.slot_start_time && pickupPayload?.child?.slot_end_time
            ? `${pickupPayload.child.slot_start_time} - ${pickupPayload.child.slot_end_time}`
            : '',
    ].filter(Boolean).join(' · ');

    const galleryInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [selectedId, setSelectedId] = useState('');
    const [originalPickupRequests, setOriginalPickupRequests] = useState([]);
    const [draftPickupDates, setDraftPickupDates] = useState([]);
    const [showActivation, setShowActivation] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [cropSource, setCropSource] = useState('');
    const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
    const [cropZoom, setCropZoom] = useState(1);
    const [cropPixels, setCropPixels] = useState(null);
    const [isApplyingCrop, setIsApplyingCrop] = useState(false);
    const [previewPhoto, setPreviewPhoto] = useState('');

    const saveMutation = useMutationWrapper(
        ['saveVisitorPickupCard'],
        saveVisitorPickupCard,
        {
            onSuccess: (result) => {
                if (!isVisitorPickupSuccess(result)) return;
                cardsQuery.refetch();
                setShowForm(false);
                setForm(EMPTY_FORM);
                setFormError('');
                setCropSource('');
                setPreviewPhoto('');
            },
        }
    );
    const activateMutation = useMutationWrapper(
        ['activateVisitorPickup'],
        activateVisitorPickup
    );
    const deactivateMutation = useMutationWrapper(
        ['deactivateVisitorPickup'],
        deactivateVisitorPickup
    );

    const isSaving = saveMutation.isLoading;
    const isActivating = activateMutation.isLoading;
    const isDeactivating = deactivateMutation.isLoading;
    const isBusy = cardsQuery.isFetching || isSaving || isActivating || isDeactivating;

    const selectedCard = cards.find((card) => String(card.card_id) === String(selectedId));
    const takenDatesForSelected = cards
        .filter((card) => String(card.card_id) !== String(selectedId))
        .flatMap((card) => getActiveRequests(card).map((item) => item.pickup_date).filter(Boolean));
    const selectedCardRequests = originalPickupRequests;
    const selectedCardDates = selectedCardRequests.map((item) => item.pickup_date).filter(Boolean);
    const selectedCardIsScheduled = selectedCardRequests.length > 0;
    const lockedPickupDates = [...new Set(
        selectedCardRequests
            .filter((item) => !item.can_deactivate || !item.request_id)
            .map((item) => item.pickup_date)
            .filter(Boolean)
    )];
    const addedPickupDates = draftPickupDates.filter((date) => !selectedCardDates.includes(date));
    const removedPickupRequests = selectedCardRequests.filter(
        (item) => item.pickup_date && !draftPickupDates.includes(item.pickup_date)
    );
    const removedRequestIds = [...new Set(
        removedPickupRequests.map((item) => item.request_id).filter((id) => id != null && id !== '')
    )];
    const hasPickupChanges = addedPickupDates.length > 0 || removedPickupRequests.length > 0;
    const selectedDateLabels = draftPickupDates
        .map((date) => allowedDates.find((item) => item.date === date)?.display || date)
        .join(', ') || 'None';

    const openAddForm = () => {
        if (cards.length >= maxCards) return;
        setForm(EMPTY_FORM);
        setFormError('');
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setFormError('');
        setCropSource('');
        setPreviewPhoto('');
    };

    const openCropper = (photo) => {
        setCropSource(photo);
        setCropPosition({ x: 0, y: 0 });
        setCropZoom(1);
        setCropPixels(null);
        setFormError('');
    };

    const applyCrop = async () => {
        if (!cropSource || !cropPixels) return;
        setIsApplyingCrop(true);
        try {
            const croppedPhoto = await createCroppedPhoto(cropSource, cropPixels);
            setForm((current) => ({ ...current, photo: croppedPhoto }));
            setCropSource('');
            setFormError('');
        } catch {
            setFormError('We could not crop that image. Please try another photo.');
        } finally {
            setIsApplyingCrop(false);
        }
    };

    const handleNativePhoto = async (source) => {
        try {
            const photo = await Camera.getPhoto({
                source,
                resultType: CameraResultType.DataUrl,
                quality: 78,
                width: 900,
                correctOrientation: true,
                allowEditing: false,
            });
            if (photo.dataUrl) {
                openCropper(photo.dataUrl);
            }
        } catch (error) {
            const message = String(error?.message || error || '');
            if (/cancel|dismiss/i.test(message)) return;
            setFormError('Photo access is unavailable. Check camera and photo permissions, then try again.');
        }
    };

    const requestPhoto = (source) => {
        if (Capacitor.isNativePlatform()) {
            handleNativePhoto(source === 'camera' ? CameraSource.Camera : CameraSource.Photos);
            return;
        }
        if (source === 'camera') cameraInputRef.current?.click();
        else galleryInputRef.current?.click();
    };

    const handleWebPhoto = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const photo = await resizeImage(file);
            openCropper(photo);
        } catch {
            setFormError('We could not read that image. Please choose another photo.');
        }
    };

    const savePerson = () => {
        if (isSaving) return;
        if (!form.photo) {
            setFormError('Add a clear photo of the pickup person.');
            return;
        }
        if (!form.name.trim() || !form.relationship) {
            setFormError('Enter the person’s name and relationship.');
            return;
        }
        if (cards.length >= maxCards) {
            setFormError(`You can add up to ${maxCards} visitor cards.`);
            return;
        }
        setFormError('');
        saveMutation.mutate({
            child: activeChild,
            parentEmail,
            visitorName: form.name.trim(),
            relationToChild: form.relationship,
            visitorPhoto: form.photo,
        });
    };

    const openActivation = (card) => {
        const requests = getActiveRequests(card);
        setSelectedId(String(card.card_id));
        setOriginalPickupRequests(requests);
        setDraftPickupDates(
            [...new Set(requests.map((item) => item.pickup_date).filter(Boolean))].sort()
        );
        setShowActivation(true);
    };

    const closeActivation = () => {
        if (isActivating || isDeactivating) return;
        setShowActivation(false);
        setSelectedId('');
        setOriginalPickupRequests([]);
        setDraftPickupDates([]);
    };

    const openHistory = (card) => {
        navigate(`/pickup-authorizations/${encodeURIComponent(card.card_id)}/history`, {
            state: { person: { id: String(card.card_id), name: card.visitor_name } },
        });
    };

    const handleCalendarDatesChange = (nextDates) => {
        if (isActivating || isDeactivating) return;
        const dates = [...new Set(nextDates)].sort();
        const blockedRemoval = selectedCardRequests.find(
            (item) => item.pickup_date
                && !dates.includes(item.pickup_date)
                && (!item.can_deactivate || !item.request_id)
        );
        if (blockedRemoval) {
            showVisitorPickupAlert('Deactivation is no longer available for this pickup.');
            return;
        }
        setDraftPickupDates(dates);
    };

    const savePickupChanges = async () => {
        if (isActivating || isDeactivating) return;
        if (!hasPickupChanges) {
            closeActivation();
            return;
        }
        if (removedPickupRequests.some((item) => !item.can_deactivate || !item.request_id)) {
            showVisitorPickupAlert('Deactivation is no longer available for one or more selected pickups.');
            return;
        }
        if (!draftPickupDates.length && !removedRequestIds.length) {
            showVisitorPickupAlert('Select a pickup person and at least one date first.');
            return;
        }
        const unavailableDate = addedPickupDates.find((date) => !selectableDates.includes(date));
        if (unavailableDate) {
            const closedDate = allowedDates.find((item) => item.date === unavailableDate);
            showVisitorPickupAlert(closedDate?.reason || 'Please choose a date that is available for visitor pickup.');
            return;
        }
        if (addedPickupDates.some((date) => takenDatesForSelected.includes(date))) {
            showVisitorPickupAlert('One or more dates are already scheduled for another pickup person.');
            return;
        }

        let activationCompleted = false;
        const apiSuccessMessages = [];
        if (addedPickupDates.length) {
            const activationResult = await activateMutation.mutateAsync({
                child: activeChild,
                parentEmail,
                cardId: selectedId,
                pickupDates: [...new Set(addedPickupDates)].sort(),
                showSuccess: false,
            });
            if (!isVisitorPickupSuccess(activationResult)) return;
            activationCompleted = true;
            if (activationResult.message) {
                apiSuccessMessages.push(String(activationResult.message).trim());
            }
        }

        if (removedRequestIds.length) {
            const deactivationResult = await deactivateMutation.mutateAsync({
                child: activeChild,
                parentEmail,
                requestIds: removedRequestIds,
                showSuccess: false,
            });
            if (!isVisitorPickupSuccess(deactivationResult)) {
                await cardsQuery.refetch();
                if (activationCompleted) {
                    showVisitorPickupAlert(
                        'New pickup dates were activated, but some previous dates could not be deactivated. Please review the updated dates.'
                    );
                }
                setShowActivation(false);
                setSelectedId('');
                setOriginalPickupRequests([]);
                setDraftPickupDates([]);
                return;
            }
            if (deactivationResult.message) {
                apiSuccessMessages.push(String(deactivationResult.message).trim());
            }
        }

        await cardsQuery.refetch();
        setShowActivation(false);
        setSelectedId('');
        setOriginalPickupRequests([]);
        setDraftPickupDates([]);
        const activatedLabel = `${addedPickupDates.length} pickup ${addedPickupDates.length === 1 ? 'date' : 'dates'}`;
        const deactivatedLabel = `${removedRequestIds.length} pickup ${removedRequestIds.length === 1 ? 'date' : 'dates'}`;
        const fallbackSuccessMessage = addedPickupDates.length && removedRequestIds.length
            ? `${activatedLabel} activated and ${deactivatedLabel} deactivated successfully.`
            : addedPickupDates.length
                ? `${activatedLabel} activated successfully.`
                : `${deactivatedLabel} deactivated successfully.`;
        showVisitorPickupAlert(
            apiSuccessMessages.filter(Boolean).join(' ') || fallbackSuccessMessage,
            'Success'
        );
    };

    const deactivatePerson = async (card) => {
        if (isDeactivating) return;
        const request = getActiveRequests(card).find((item) => item.can_deactivate && item.request_id);
        if (!request?.request_id) {
            showVisitorPickupAlert(
                getActiveRequests(card).length
                    ? 'Deactivation is no longer available for this pickup.'
                    : 'Unable to deactivate this pickup. Please refresh and try again.'
            );
            return;
        }
        const result = await deactivateMutation.mutateAsync({
            child: activeChild,
            parentEmail,
            requestIds: [request.request_id],
        });
        if (isVisitorPickupSuccess(result)) await cardsQuery.refetch();
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50">
            <div className="sticky top-0 z-20 flex items-center space-x-3 border-b bg-white p-4">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="rounded-full p-1 text-gray-600 hover:bg-gray-100 active:bg-gray-100"
                    aria-label="Go back"
                >
                    <ChevronLeft className="h-6 w-6" />
                </button>
                <h2 className="cursor-pointer text-lg font-bold text-gray-800" onClick={() => navigate(-1)}>
                    Visitor Pickup
                </h2>
            </div>

            {isBusy && <SpinnerLoading />}

            <main className="space-y-4 p-4 pb-10 lg:space-y-4 lg:p-4">
                <section>
                    <h2 className="text-lg font-bold leading-6 text-gray-900">
                        {childName}
                    </h2>
                    <p className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                        {programLabel}
                    </p>
                </section>

                {cardsQuery.isFetching && !pickupPayload
                    ? null
                    : cardsError && !pickupPayload
                        ? null
                        : cards.length === 0 ? (
                    <section className="flex min-h-[60vh] flex-col items-center justify-center px-5 text-center md:min-h-[50vh]">
                        <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-cyan-50">
                            <UserCheck className="h-12 w-12 text-cyan-500" strokeWidth={1.7} />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Add a trusted pickup person</h2>
                        <p className="mt-2 max-w-sm text-sm leading-6 text-gray-600">
                            Add someone who may collect your child from the center. You can add up to {maxCards} people.
                        </p>
                        <button
                            type="button"
                            onClick={openAddForm}
                            className="mt-7 flex w-full max-w-sm items-center justify-center gap-2 rounded-xl bg-cyan-500 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-cyan-600 active:bg-cyan-600 md:w-auto md:min-w-[240px]"
                        >
                            <Plus className="h-5 w-5" />
                            Add pickup person
                        </button>
                    </section>
                ) : (
                    <>
                        <section className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-4 md:p-5">
                            <div className="flex gap-3">
                                <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-cyan-600" />
                                <div>
                                    <h2 className="font-semibold text-gray-900">Who can pick up your child?</h2>
                                    <p className="mt-1 text-sm leading-5 text-gray-600">
                                        Tap a card to view its history. Use the card action to schedule an active person for one or more dates.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <div className="flex items-center justify-between">
                            <h2 className="font-bold text-gray-900">Trusted pickup people</h2>
                            <span className="text-xs font-medium text-gray-500">
                                {cards.length} of {maxCards} added
                            </span>
                        </div>

                        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Pickup people">
                            {cards.map((card) => {
                                const requests = getActiveRequests(card);
                                const isScheduled = requests.length > 0;
                                const canDeactivate = requests.some((item) => item.can_deactivate);
                                const untilDisplay = requests.find((item) => item.can_deactivate)?.deactivate_until_display
                                    || requests[0]?.deactivate_until_display;
                                return (
                                    <div
                                        key={card.card_id}
                                        className={`flex h-full w-full flex-col overflow-hidden rounded-2xl border-2 p-4 text-left shadow-sm transition md:p-5 ${
                                            isScheduled
                                                ? 'border-cyan-500 bg-cyan-50'
                                                : 'border-gray-300 bg-white'
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <button
                                                type="button"
                                                onClick={() => openHistory(card)}
                                                className="flex min-w-0 flex-1 items-start gap-3 text-left"
                                                aria-label={`View pickup history for ${card.visitor_name}`}
                                            >
                                                <img
                                                    src={card.visitor_photo}
                                                    alt={card.visitor_name}
                                                    className="h-16 w-16 shrink-0 rounded-full border-2 border-white object-cover shadow md:h-20 md:w-20"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="text-lg font-bold leading-6 text-gray-900">{card.visitor_name}</h3>
                                                    <p className="mt-0.5 text-sm text-gray-600">{card.relation_to_child}</p>
                                                    <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                                        Added {card.added_on_display}
                                                    </p>
                                                </div>
                                            </button>

                                            <div className="flex shrink-0 flex-col items-end gap-2">
                                                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                                    isScheduled
                                                        ? 'bg-cyan-600 text-white'
                                                        : 'bg-gray-200 text-gray-600'
                                                }`}>
                                                    {card.status}
                                                </span>
                                            </div>
                                        </div>

                                        {isScheduled ? (
                                            <div className="mt-auto space-y-3 border-t border-cyan-200 pt-3">
                                                {requests.length === 1 ? (
                                                    <div className="flex items-end justify-between gap-3">
                                                        <div>
                                                            <span className="block text-[11px] font-semibold uppercase tracking-wider text-cyan-700">
                                                                Pickup on
                                                            </span>
                                                            <span className="mt-1 block font-bold text-gray-900">
                                                                {requests[0].pickup_date_display || requests[0].pickup_date}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => deactivatePerson(card)}
                                                            disabled={isDeactivating || !canDeactivate}
                                                            className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50 active:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                                                        >
                                                            <span>{isDeactivating ? 'Deactivating…' : 'Deactivate'}</span>
                                                            {untilDisplay && (
                                                                <span className="text-[10px] font-medium uppercase tracking-wide text-red-400">
                                                                    Until {untilDisplay}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <span className="block text-[11px] font-semibold uppercase tracking-wider text-cyan-700">
                                                                Pickup on
                                                            </span>
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {requests.map((item) => (
                                                                    <span
                                                                        key={item.request_id || item.pickup_date}
                                                                        className="rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-xs font-semibold text-cyan-800"
                                                                    >
                                                                        {item.pickup_date_display || item.pickup_date}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => openActivation(card)}
                                                            disabled={!canDeactivate}
                                                            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50 active:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 md:w-auto md:self-start"
                                                        >
                                                            <span>Deactivate</span>
                                                            {untilDisplay && (
                                                                <span className="text-[10px] font-medium uppercase tracking-wide text-red-400">
                                                                    Until {untilDisplay}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => openActivation(card)}
                                                className="mt-auto flex w-full border-t border-gray-200 pt-3 text-left"
                                            >
                                                <span className="font-semibold text-cyan-700 hover:text-cyan-800">Activate for pickup dates →</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        {cards.length < maxCards && (
                            <button
                                type="button"
                                onClick={openAddForm}
                                className="flex min-h-[180px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-cyan-300 bg-white px-4 py-3 font-semibold text-cyan-700 hover:bg-cyan-50 active:bg-cyan-50 md:min-h-full"
                            >
                                <Plus className="h-5 w-5" />
                                Add pickup person
                            </button>
                        )}
                        </section>

                    </>
                )}
            </main>

            {showActivation && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/45 md:items-center md:p-6">
                    <div className="max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl md:max-w-lg md:rounded-3xl lg:max-w-xl">
                        <div className="flex items-start justify-between gap-3">
                            <h2 className="text-lg font-bold text-gray-900">
                                {selectedCardIsScheduled ? 'Edit pickup dates' : 'Activate pickup person'}
                            </h2>
                            <button
                                type="button"
                                onClick={closeActivation}
                                disabled={isActivating || isDeactivating}
                                className="rounded-full bg-gray-100 p-2 text-gray-600"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {selectedCard && (
                            <div className={`mt-5 flex items-center gap-3 rounded-2xl border-2 p-4 ${
                                selectedCardIsScheduled
                                    ? 'border-cyan-500 bg-cyan-50'
                                    : 'border-gray-300 bg-white'
                            }`}>
                                <img
                                    src={selectedCard.visitor_photo}
                                    alt={selectedCard.visitor_name}
                                    className="h-16 w-16 shrink-0 rounded-full border-2 border-white object-cover shadow"
                                />
                                <div className="min-w-0 flex-1">
                                    <h3 className="text-lg font-bold leading-6 text-gray-900">{selectedCard.visitor_name}</h3>
                                    <p className="mt-0.5 text-sm text-gray-600">{selectedCard.relation_to_child}</p>
                                    <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                        Added {selectedCard.added_on_display}
                                    </p>
                                </div>
                                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                    selectedCardIsScheduled
                                        ? 'bg-cyan-600 text-white'
                                        : 'bg-gray-200 text-gray-600'
                                }`}>
                                    {selectedCard.status}
                                </span>
                            </div>
                        )}

                        <div className="mt-4">
                            <p className="mb-2 text-sm font-semibold text-gray-800">
                                Pickup dates
                            </p>
                            <InlinePickupCalendar
                                value={draftPickupDates}
                                onChange={handleCalendarDatesChange}
                                allowedDates={allowedDates}
                                unavailableDates={takenDatesForSelected}
                                ownedDates={selectedCardDates}
                                lockedDates={lockedPickupDates}
                            />
                            <div className="mt-3 flex items-start gap-2 rounded-xl bg-cyan-50 px-3 py-2.5 text-sm text-cyan-800">
                                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                    Selected: <strong>{selectedDateLabels}</strong>
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={savePickupChanges}
                            disabled={isActivating || isDeactivating || !selectedId || !hasPickupChanges}
                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3.5 font-bold text-white shadow-sm hover:bg-cyan-600 active:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                        >
                            <ShieldCheck className="h-5 w-5" />
                            {isActivating
                                ? 'Saving…'
                                : isDeactivating
                                    ? 'Updating…'
                                    : selectedCardIsScheduled ? 'Save changes' : 'Activate for pickup'}
                        </button>
                        <p className="mt-3 text-center text-xs leading-5 text-gray-500">
                            {selectedCardIsScheduled
                                ? 'Add or remove pickup dates, then tap Save changes. Center staff will verify this person against their photo.'
                                : 'Choose from the available pickup dates shown above. Center staff will verify this person against their photo.'}
                        </p>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/45 md:items-center md:p-6">
                    <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl md:max-w-lg md:rounded-3xl lg:max-w-xl">
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    Add pickup person
                                </h2>
                                <p className="text-xs text-gray-500">Add a clear, recent photo</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeForm}
                                className="rounded-full bg-gray-100 p-2 text-gray-600"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-4 p-5">
                            <div className="flex flex-col items-center">
                                <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-cyan-300 bg-cyan-50">
                                    {form.photo ? (
                                        <img src={form.photo} alt="Pickup person preview" className="h-full w-full object-cover" />
                                    ) : (
                                        <ImagePlus className="h-10 w-10 text-cyan-500" />
                                    )}
                                </div>
                                {form.photo && (
                                    <div className="mt-3 flex items-center gap-4 text-sm font-semibold text-cyan-700">
                                        <button
                                            type="button"
                                            onClick={() => setPreviewPhoto(form.photo)}
                                            className="flex items-center gap-1.5 active:text-cyan-900"
                                        >
                                            <Eye className="h-4 w-4" />
                                            Preview
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openCropper(form.photo)}
                                            className="active:text-cyan-900"
                                        >
                                            Crop again
                                        </button>
                                    </div>
                                )}
                                <div className="mt-4 grid w-full grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => requestPhoto('camera')}
                                        className="flex items-center justify-center gap-2 rounded-xl border border-cyan-300 px-3 py-3 text-sm font-semibold text-cyan-700 active:bg-cyan-50"
                                    >
                                        <CameraIcon className="h-5 w-5" />
                                        Take photo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => requestPhoto('gallery')}
                                        className="flex items-center justify-center gap-2 rounded-xl border border-cyan-300 px-3 py-3 text-sm font-semibold text-cyan-700 active:bg-cyan-50"
                                    >
                                        <ImagePlus className="h-5 w-5" />
                                        Choose photo
                                    </button>
                                </div>
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleWebPhoto}
                                    className="hidden"
                                />
                                <input
                                    ref={galleryInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleWebPhoto}
                                    className="hidden"
                                />
                            </div>

                            <div>
                                <label htmlFor="pickup-name" className="mb-1.5 block text-sm font-semibold text-gray-700">
                                    Full name
                                </label>
                                <input
                                    id="pickup-name"
                                    value={form.name}
                                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                                    placeholder="e.g. Anita Sharma"
                                    className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                                />
                            </div>

                            <div>
                                <label htmlFor="pickup-relationship" className="mb-1.5 block text-sm font-semibold text-gray-700">
                                    Relationship to child
                                </label>
                                <select
                                    id="pickup-relationship"
                                    value={form.relationship}
                                    onChange={(event) => setForm({ ...form, relationship: event.target.value })}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                                >
                                    <option value="">Select relationship</option>
                                    {relationshipOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                    {form.relationship && !relationshipOptions.includes(form.relationship) && (
                                        <option value={form.relationship}>{form.relationship}</option>
                                    )}
                                </select>
                            </div>

                            {formError && (
                                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                                    {formError}
                                </p>
                            )}

                            <div className="pt-1">
                                <button
                                    type="button"
                                    onClick={savePerson}
                                    disabled={isSaving}
                                    className="w-full rounded-xl bg-cyan-500 px-4 py-3.5 font-bold text-white shadow-sm hover:bg-cyan-600 active:bg-cyan-600 disabled:bg-gray-300"
                                >
                                    {isSaving ? 'Saving…' : 'Save pickup person'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {cropSource && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 md:items-center md:p-6">
                    <div className="w-full overflow-hidden rounded-t-3xl bg-white shadow-2xl md:max-w-lg md:rounded-3xl lg:max-w-xl">
                        <div className="flex items-center justify-between border-b px-5 py-4">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Crop &amp; preview</h2>
                                <p className="text-xs text-gray-500">Move and zoom to frame the face clearly</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setCropSource('')}
                                className="rounded-full bg-gray-100 p-2 text-gray-600"
                                aria-label="Close cropper"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="relative h-[52vh] min-h-[300px] max-h-[500px] bg-gray-900">
                            <Cropper
                                image={cropSource}
                                crop={cropPosition}
                                zoom={cropZoom}
                                aspect={1}
                                cropShape="round"
                                showGrid
                                onCropChange={setCropPosition}
                                onZoomChange={setCropZoom}
                                onCropComplete={(_, croppedAreaPixels) => setCropPixels(croppedAreaPixels)}
                            />
                        </div>

                        <div className="space-y-4 p-5">
                            <div>
                                <label htmlFor="photo-zoom" className="mb-2 block text-sm font-semibold text-gray-700">
                                    Zoom
                                </label>
                                <input
                                    id="photo-zoom"
                                    type="range"
                                    min="1"
                                    max="3"
                                    step="0.05"
                                    value={cropZoom}
                                    onChange={(event) => setCropZoom(Number(event.target.value))}
                                    className="w-full accent-cyan-500"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCropSource('')}
                                    className="rounded-xl border border-gray-300 px-4 py-3 font-semibold text-gray-700 active:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={applyCrop}
                                    disabled={isApplyingCrop || !cropPixels}
                                    className="rounded-xl bg-cyan-500 px-4 py-3 font-bold text-white active:bg-cyan-600 disabled:bg-gray-300"
                                >
                                    {isApplyingCrop ? 'Applying…' : 'Use photo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {previewPhoto && (
                <div className="fixed inset-0 z-[90] flex flex-col bg-black/90">
                    <div className="flex items-center justify-between px-4 py-4 text-white">
                        <h2 className="font-semibold">Photo preview</h2>
                        <button
                            type="button"
                            onClick={() => setPreviewPhoto('')}
                            className="rounded-full bg-white/15 p-2"
                            aria-label="Close preview"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <div className="flex flex-1 items-center justify-center p-5">
                        <img
                            src={previewPhoto}
                            alt="Pickup person full preview"
                            className="max-h-full max-w-full rounded-2xl object-contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PickupAuthorizations;
